// legacy/navigation-bridge.js — Navigation store ↔ state.js bridge
//
// Bidirectional sync so navigation.store and state.js stay consistent
// during the transition period.  Both sides are authoritative for
// different consumers: state.js is the legacy truth (app.js, navigation.js,
// step modules); navigation.store is the new truth (future facade/store
// consumers).
//
// Seed: on init, state.js → navigation.store (state.js is the existing
// source of truth).
// Runtime: state.js changes → push to store; store changes → push to state.js.
//
// @module

import { getState, setState, subscribe } from '../state.js';
import * as navigationStore from '../interaction/stores/navigation.store.js';

let _destroyed = false;

/**
 * Seed navigation.store from state.js (one-way, state.js → store).
 * Call once at app startup before any subscriptions fire.
 */
export function seedNavigationStore() {
  navigationStore.replaceNavigation({
    currentStep: getState('currentStep'),
    highestStep: getState('highestStep'),
  });
}

/**
 * Start bidirectional sync. Returns a teardown function.
 * After calling this, changes on either side propagate to the other.
 */
export function startNavigationBridge() {
  // state.js → store
  const unsubCurrent = subscribe('state:currentStepChanged', ({ curr }) => {
    if (_destroyed) return;
    navigationStore.setCurrentStep(curr);
  });
  const unsubHighest = subscribe('state:highestStepChanged', ({ curr }) => {
    if (_destroyed) return;
    navigationStore.replaceNavigation({ highestStep: curr });
  });

  // store → state.js (skip if value already matches to avoid loops)
  const unsubStore = navigationStore.subscribe(() => {
    if (_destroyed) return;
    const snap = navigationStore.getSnapshot();
    if (getState('currentStep') !== snap.currentStep) {
      setState('currentStep', snap.currentStep);
    }
    if (getState('highestStep') !== snap.highestStep) {
      setState('highestStep', snap.highestStep);
    }
  });

  return function destroy() {
    _destroyed = true;
    unsubCurrent();
    unsubHighest();
    unsubStore();
  };
}
